import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ShieldCheck, ArrowsLeftRight, PaperPlaneTilt, ArrowRight } from "@phosphor-icons/react";

/**
 * Dashboard "Pending Approvals" tile — shows queue counts for whoever is
 * logged in (incoming transfers I can decide on + sales I can verify +
 * outgoing transfers awaiting the other branch). Hidden entirely when all
 * counters are 0 AND the user can't verify sales — keeps the dashboard
 * clean for users who aren't approvers.
 */
export default function PendingApprovalsWidget() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get("/dashboard/pending-approvals")
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setData(null));
    return () => {
      alive = false;
    };
  }, []);

  if (!data) return null;
  const totalActionable = data.incoming_transfers + data.pending_sales;
  // Hide the whole widget if the user has nothing to act on AND nothing
  // outgoing waiting — keeps Salesperson dashboards uncluttered.
  if (totalActionable === 0 && data.outgoing_transfers_pending === 0) return null;

  return (
    <div className="bg-canvas-card border border-canvas-line p-5" data-testid="pending-approvals-widget">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted mb-1">
            06 / My Pending Approvals
          </div>
          <h3 className="font-display text-lg font-semibold text-ink">Action queue</h3>
        </div>
        {totalActionable > 0 && (
          <span
            className="font-mono text-[10px] uppercase tracking-wider px-2 py-1 border border-destructive bg-destructive/5 text-destructive"
            data-testid="pending-approvals-total"
          >
            {totalActionable} awaiting you
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <ApprovalTile
          to="/inventory"
          icon={ArrowsLeftRight}
          label="Incoming Transfers"
          count={data.incoming_transfers}
          hint="Vehicles moving INTO your branch · open Transfer History to approve"
          actionable
          testId="pending-incoming-transfers"
        />
        <ApprovalTile
          to="/sales?status=pending_approval"
          icon={ShieldCheck}
          label="Sales to Verify"
          count={data.pending_sales}
          hint={
            data.can_verify_sales
              ? "Pending sales waiting your verification"
              : "You don't have sales.verify permission"
          }
          actionable={data.can_verify_sales}
          testId="pending-sales-verify"
        />
        <ApprovalTile
          to="/inventory"
          icon={PaperPlaneTilt}
          label="My Outgoing Transfers"
          count={data.outgoing_transfers_pending}
          hint="Transfers YOU initiated — awaiting the other branch"
          actionable={false}
          testId="pending-outgoing-transfers"
        />
      </div>
    </div>
  );
}

function ApprovalTile({ to, icon: Icon, label, count, hint, actionable, testId }) {
  const isHot = actionable && count > 0;
  const isMuted = !actionable || count === 0;
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`flex flex-col gap-2 p-4 border transition-colors duration-150 ${
        isHot
          ? "border-destructive bg-destructive/5 hover:bg-destructive/10"
          : "border-canvas-line hover:border-ink"
      }`}
    >
      <div className="flex items-center justify-between">
        <Icon size={16} weight="bold" className={isHot ? "text-destructive" : "text-ink-muted"} />
        <ArrowRight size={12} weight="bold" className={isMuted ? "text-ink-subtle" : "text-ink"} />
      </div>
      <div className={`font-display text-2xl font-bold tabular-nums ${isMuted ? "text-ink-muted" : "text-ink"}`}>
        {count}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="text-[11px] text-ink-subtle leading-tight">{hint}</div>
    </Link>
  );
}
